import type { components, paths } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type PlannerStateResponse =
  components['schemas']['PlannerStateResponse'];
export type PlannerState = PlannerStateResponse['data'];
export type PlannerGameweek = PlannerState['gameweeks'][number];
export type PlannerSquadPlayer = PlannerGameweek['starting'][number];
export type PlannerBenchPlayer = PlannerGameweek['bench'][number];
export type PlannerChip = PlannerState['chips'][number];

export type PlannerStateQuery = NonNullable<
  paths['/users/me/planner/state']['get']['parameters']['query']
>;

export type FetchPlannerStateOptions = PlannerStateQuery & {
  accessToken: string;
};

export type PlannerEvent = components['schemas']['AppendEventsBody'][number];
export type TransferEvent = Extract<PlannerEvent, { type: 'TRANSFER' }>;
/**
 * A swap names exactly the two slots to exchange, and the spec still says so:
 * `minItems: 2, maxItems: 2`.
 *
 * The generated type no longer does. A fixed-length array is `prefixItems` in
 * OpenAPI 3.1, which `openapi-typescript` renders as a tuple, and the backend's
 * spec emitter has begun writing the 3.0 spelling — a plain `items` with the
 * bounds alongside — which it can only render as `number[]`. Restoring the
 * bound here keeps a swap that names one slot, or three, a compile error rather
 * than a request the backend rejects.
 *
 * Delete once the emitter writes `prefixItems` again and the generated type is
 * a tuple on its own.
 */
export type SwapEvent = Omit<
  Extract<PlannerEvent, { type: 'SWAP' }>,
  'slots'
> & {
  slots: [number, number];
};
export type SetCaptainEvent = Extract<PlannerEvent, { type: 'SET_CAPTAIN' }>;
export type ActivateChipEvent = Extract<
  PlannerEvent,
  { type: 'ACTIVATE_CHIP' }
>;
/** Returns a chip scheduled for `gameweek` to "not set". */
export type DeactivateChipEvent = Extract<
  PlannerEvent,
  { type: 'DEACTIVATE_CHIP' }
>;
export type ChipType = ActivateChipEvent['chip'];

export type UpsertPlannerBody = components['schemas']['UpsertPlannerBody'];
export type OptimiseBody = components['schemas']['OptimiseBody'];

export type PlannerErrorBody = components['schemas']['ErrorResponse']['error'];
export type PlannerViolation = NonNullable<
  PlannerErrorBody['violations']
>[number];
/**
 * Field-level rejections. The squad upsert reports each broken FPL rule here
 * ("Max 3 players per club, but Man City has 4") while the envelope's own
 * message stays generic, so surfacing these is the difference between the user
 * knowing what to fix and reading "Squad validation failed".
 */
export type PlannerErrorDetail = NonNullable<
  PlannerErrorBody['details']
>[number];

const NOT_FOUND_STATUS = 404;
const SEASON_CLOSED_STATUS = 422;
const CONFLICT_STATUS = 409;

export class PlannerApiError extends Error {
  status: number;
  violations?: PlannerViolation[];
  details?: PlannerErrorDetail[];
  code?: string;

  constructor(
    status: number,
    message: string,
    options: {
      violations?: PlannerViolation[];
      details?: PlannerErrorDetail[];
      code?: string;
    } = {}
  ) {
    super(message);
    this.name = 'PlannerApiError';
    this.status = status;
    this.violations = options.violations;
    this.details = options.details;
    this.code = options.code;
  }
}

export const isPlannerNotFound = (
  candidate: unknown
): candidate is PlannerApiError =>
  candidate instanceof PlannerApiError && candidate.status === NOT_FOUND_STATUS;

export const isPlannerSeasonClosed = (
  candidate: unknown
): candidate is PlannerApiError =>
  candidate instanceof PlannerApiError &&
  candidate.status === SEASON_CLOSED_STATUS;

export const isPlannerConflict = (
  candidate: unknown
): candidate is PlannerApiError =>
  candidate instanceof PlannerApiError && candidate.status === CONFLICT_STATUS;

const toPlannerError = (
  status: number,
  fallbackMessage: string,
  body: unknown
): PlannerApiError => {
  const errorBody = (body as { error?: PlannerErrorBody })?.error;
  return new PlannerApiError(status, errorBody?.message ?? fallbackMessage, {
    violations: errorBody?.violations,
    details: errorBody?.details,
    code: errorBody?.code
  });
};

export const fetchPlannerState = async ({
  accessToken,
  ...query
}: FetchPlannerStateOptions): Promise<PlannerStateResponse> => {
  const { data, error, response } = await ffhClient().GET(
    '/users/me/planner/state',
    {
      headers: authHeader(accessToken),
      params: { query }
    }
  );

  if (error || !data) {
    throw toPlannerError(
      response?.status ?? 0,
      'Failed to load planner state',
      error
    );
  }

  return data;
};

export const appendPlannerEvents = async ({
  accessToken,
  events
}: {
  accessToken: string;
  events: PlannerEvent[];
}): Promise<void> => {
  const { error, response } = await ffhClient().POST(
    '/users/me/planner/events',
    {
      headers: authHeader(accessToken),
      body: events
    }
  );

  // openapi-fetch leaves `error` undefined on a non-2xx response with an empty
  // body, so checking `error` alone reports those failures as successes
  if (error || !response?.ok) {
    throw toPlannerError(
      response?.status ?? 0,
      'Failed to append planner events',
      error
    );
  }
};

export const optimisePlannerLineup = async ({
  accessToken,
  gameweek
}: {
  accessToken: string;
  gameweek: number;
}): Promise<void> => {
  const { error, response } = await ffhClient().POST(
    '/users/me/planner/optimise',
    {
      headers: authHeader(accessToken),
      body: { gameweek }
    }
  );

  if (error || !response?.ok) {
    throw toPlannerError(
      response?.status ?? 0,
      'Failed to optimise lineup',
      error
    );
  }
};

export const setPlannerBank = async ({
  accessToken,
  gameweek,
  bank
}: {
  accessToken: string;
  gameweek: number;
  bank: number;
}): Promise<void> => {
  const { error, response } = await ffhClient().PATCH(
    '/users/me/planner/bank',
    {
      headers: authHeader(accessToken),
      body: { gameweek, bank }
    }
  );

  if (error || !response?.ok) {
    throw toPlannerError(
      response?.status ?? 0,
      'Failed to update bank amount',
      error
    );
  }
};

export const upsertPlanner = async ({
  accessToken,
  body,
  signal
}: {
  accessToken: string;
  body: UpsertPlannerBody;
  signal?: AbortSignal;
}): Promise<void> => {
  const { error, response } = await ffhClient().PUT('/users/me/planner', {
    headers: authHeader(accessToken),
    body,
    signal
  });

  if (error || !response?.ok) {
    throw toPlannerError(
      response?.status ?? 0,
      'Failed to upsert planner',
      error
    );
  }
};
